/**
 * IS_PREVIEW fixtures for the marketplace-first Pro Home (plan 443).
 *
 * `/home?home=<name>` picks one of the named fixtures in
 * `testdata/pro_home_fixtures.json`; this module is the one place that turns
 * that choice into every input Home needs, so a preview renders with ZERO
 * network calls:
 *
 *   - the Home summary and pipeline summary: the fixture itself
 *     (`loadPreviewHomeFixture`, read by `stores/proHome.ts`);
 *   - the go-live gate: `flagoff` is gate `off`, everything else `on`
 *     (`previewHomeGate`, read by `useMarketplaceGate`);
 *   - the pending-requests list, consistent with `requests.pending`
 *     (`previewRequestsFor`, read by `useMarketplaceRequestList`);
 *   - the Today queue, consistent with the fixture's pipeline and requests
 *     (`previewTodayQueueFor`, read by `stores/today.ts`);
 *   - the clock (`homeNow`): fixtures carry absolute timestamps, so preview
 *     pins "now" before all of them and every deadline reads as upcoming no
 *     matter when the preview is opened.
 *
 * Every caller guards on `IS_PREVIEW` first; nothing here runs in production.
 */
import type { MarketplaceConnectionRequest } from "./api";
import { IS_PREVIEW, previewQueryParam } from "./pro-mode";
import type {
  ProHomeFixture,
  ProHomeFixtureName,
  ProHomeFixtures,
  ProHomeSummary,
} from "./pro-home-types";
import type { TodayQueueVM } from "./view-model";

export const PRO_HOME_FIXTURE_NAMES: readonly ProHomeFixtureName[] = [
  "busy",
  "setup",
  "quiet",
  "hidden",
  "discoverable",
  "fix",
  "paused",
  "flag-off",
  "empty",
  "degraded",
];

/** Before every timestamp in the fixtures (the earliest is an offer expiring 2026-10-08T09:00Z). */
export const PREVIEW_HOME_NOW = new Date("2026-10-08T07:00:00Z");

const HOUR_MS = 3_600_000;

/** Home's clock: pinned under IS_PREVIEW, the real clock otherwise. */
export function homeNow(): Date {
  return IS_PREVIEW ? new Date(PREVIEW_HOME_NOW) : new Date();
}

/** `?home=` resolved to a fixture name: `flagoff` aliases `flag-off`, default and unknown are `busy`. */
export function previewHomeName(): ProHomeFixtureName {
  const raw = previewQueryParam("home")?.trim().toLowerCase();
  if (!raw) return "busy";
  const name = raw === "flagoff" ? "flag-off" : raw;
  return PRO_HOME_FIXTURE_NAMES.find((n) => n === name) ?? "busy";
}

/** The go-live gate the preview simulates. */
export function previewHomeGate(): "on" | "off" {
  return previewHomeName() === "flag-off" ? "off" : "on";
}

/** The fixture is loaded on demand so it never ships in the production bundle's main chunk. */
export async function loadPreviewHomeFixture(): Promise<ProHomeFixture> {
  const { default: raw } = await import("../../testdata/pro_home_fixtures.json");
  return (raw as unknown as ProHomeFixtures)[previewHomeName()];
}

/**
 * The pending requests behind `requests.pending`: that many rows, the first
 * expiring at `earliest_expires_at` and each next one later, so the hero's
 * "first expires" and the Needs stream's deadlines agree. A null Home response
 * or failed `requests` section has none.
 */
export function previewRequestsFor(home: ProHomeSummary | null): MarketplaceConnectionRequest[] {
  const pending = home?.requests?.pending ?? 0;
  const first = Date.parse(home?.requests?.earliest_expires_at ?? "");
  if (pending <= 0 || Number.isNaN(first)) return [];
  return Array.from({ length: pending }, (_, i) => {
    const tier = i % 2 === 0 ? "t2" : "t1";
    const net = tier === "t2" ? 10000 : 5000;
    return {
      id: `preview-request-${i + 1}`,
      role: "agent",
      buyer_tier: tier,
      status: "pending_pro",
      passport_id: `preview-passport-${i + 1}`,
      display_code: `BP-${5101 + i}`,
      fee_quote: {
        rate_card_id: "preview-rate-card",
        cause: "marketplace",
        tier,
        list_cents: net,
        discount_cents: 0,
        net_cents: net,
      },
      expires_at: new Date(first + i * 6 * HOUR_MS).toISOString(),
      created_at: new Date(PREVIEW_HOME_NOW.getTime() - (i + 1) * HOUR_MS).toISOString(),
    };
  });
}

/** Whether the browser is on Home (or forcing a Home fixture), where the Today queue must match it. */
function homePreviewActive(): boolean {
  if (previewQueryParam("home") !== null) return true;
  return typeof window !== "undefined" && window.location.pathname === "/home";
}

/**
 * Shape the preview Today queue to the Home fixture so the book hero, the
 * Needs stream and the pipeline strip describe the same pro: linked clients
 * from the pipeline, the requests summary from the fixture (none when the gate
 * is off), and no CRM queue at all for `empty`. Off Home the queue is returned
 * untouched, so every other preview surface keeps its own fixture.
 */
export async function previewTodayQueueFor(base: TodayQueueVM): Promise<TodayQueueVM> {
  if (!homePreviewActive()) return base;
  const fixture = await loadPreviewHomeFixture();
  const stages = Object.entries(fixture.pipeline.stages)
    // Closed this year is history, not part of the live book.
    .filter(([stage]) => stage !== "closed_ytd")
    .reduce((sum, [, split]) => sum + split.marketplace + split.invited, 0);
  const pending = fixture.home?.requests?.pending;
  const queue: TodayQueueVM = {
    ...base,
    linkedCount: stages,
    marketplaceRequests:
      pending === undefined
        ? undefined
        : { count: pending, topFeeCents: pending > 0 ? 10000 : undefined },
  };
  if (previewHomeName() === "empty") {
    return { ...queue, groups: [], snoozed: [], drafts: [], openCount: 0 };
  }
  return queue;
}
